// The left nav, "Account sections", as Account.dc.html draws it: the page's sections as anchors,
// the one in view drawn in full ink. A click glides to the section (or jumps, for anyone who asks
// their device for less motion) and puts its name in the address, so /account#data opens there.
// At phone width it's a row along the top of the page that stays in view while the page scrolls, on
// 2 lines where its links don't all fit on 1 (account.css), so every link is always in sight.
import { useEffect, useRef, useState } from 'react'

const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

export function scrollToSection(id, smooth) {
  const el = document.getElementById(id)
  if (!el) return false
  el.scrollIntoView({ behavior: smooth && !reducedMotion() ? 'smooth' : 'auto', block: 'start' })
  return true
}

// The section in view: the last whose top has passed the line just under the top of the window,
// or the last section once the page is scrolled to its end. After a click on the nav, the section
// clicked stays current until the member scrolls the page themselves, so a short last section
// doesn't take over the highlight when the page can't scroll any further.
function useCurrentSection(ids, offset) {
  const [current, setCurrent] = useState(ids[0])
  const pinned = useRef(null)
  const key = ids.join(',')
  useEffect(() => {
    const list = key.split(',')
    let frame = 0
    function measure() {
      frame = 0
      if (pinned.current && list.includes(pinned.current)) { setCurrent(pinned.current); return }
      const atEnd = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2
      let pick = list[0]
      for (const id of list) {
        const el = document.getElementById(id)
        if (el && el.getBoundingClientRect().top <= offset) pick = id
      }
      if (atEnd && window.scrollY > 0) pick = list[list.length - 1]
      setCurrent(pick)
    }
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(measure) }
    const unpin = () => { pinned.current = null }
    measure()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    window.addEventListener('wheel', unpin, { passive: true })
    window.addEventListener('touchstart', unpin, { passive: true })
    window.addEventListener('keydown', unpin)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      window.removeEventListener('wheel', unpin)
      window.removeEventListener('touchstart', unpin)
      window.removeEventListener('keydown', unpin)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [key, offset])
  const pin = id => { pinned.current = id; setCurrent(id) }
  const isPinned = id => pinned.current === id
  return [current, pin, isPinned]
}

// The phone nav's height on 1 line (8px, a 40px link, 8px and its 1px rule), until it's measured
const PHONE_NAV_HEIGHT = 57

export function AccountNav({ sections, phone, label }) {
  const navRef = useRef(null)
  const [navHeight, setNavHeight] = useState(PHONE_NAV_HEIGHT)

  // On a phone the nav is 1 line tall or 2, as its links fit the width, and stays at the top of the
  // window. Its height goes on the page as --ac-nav-h, so a section glided to lands 15px under it
  // (account.css), and the section in view is the last whose top has passed 39px under it.
  useEffect(() => {
    const nav = navRef.current
    if (!phone || !nav) return undefined
    const page = nav.parentElement
    const measure = () => {
      page.style.setProperty('--ac-nav-h', `${nav.offsetHeight}px`)
      setNavHeight(nav.offsetHeight)
    }
    measure()
    const watch = new ResizeObserver(measure)
    watch.observe(nav)
    return () => {
      watch.disconnect()
      page.style.removeProperty('--ac-nav-h')
    }
  }, [phone])

  const [current, pin, isPinned] = useCurrentSection(sections.map(s => s.id), phone ? navHeight + 39 : 120)

  function go(e, id) {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    e.preventDefault()
    if (!scrollToSection(id, true)) return
    pin(id)
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#${id}`)
    // The glide's end is worked out when it starts. If the page's own fonts are still loading, the
    // text above grows when they land and the section ends up lower, so it's lined up again then,
    // unless the member has scrolled for themselves since.
    if (document.fonts && document.fonts.status !== 'loaded') {
      document.fonts.ready.then(() => { if (isPinned(id)) scrollToSection(id, true) })
    }
  }

  return (
    <nav ref={navRef} className={phone ? 'ac-nav ac-nav--phone' : 'ac-nav'} aria-label={label}>
      {sections.map(s => (
        <a key={s.id} href={`#${s.id}`} className="ac-nav-link" aria-current={current === s.id ? 'location' : undefined}
          onClick={e => go(e, s.id)}>
          {s.label}
        </a>
      ))}
    </nav>
  )
}
